/**
 * @file     useLiveChannel.ts
 * @purpose  The single client primitive for live updates: subscribe to a topic,
 *           reconcile on every edge that can lose an event.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import { getSupabaseBrowserClient } from '@/core/supabase/client';
import { LIVE_EVENT, type LiveResource, type LiveChangePayload } from '@/services/realtime/topics';

/** Poll cadence while the socket is down. Not a steady-state cost: it only runs
 *  when a subscription is not established. */
const DISCONNECTED_POLL_MS = 30_000;
/** Events inside this window after a refetch collapse into one trailing refetch. */
const COALESCE_MS = 1_000;

export type UseLiveChannelArgs = {
  /** Built with `topics.*` from @/services/realtime/topics. Null disables the
   *  subscription (e.g. before the user id is known). */
  topic: string | null;
  /** Only signals for this resource trigger onChange. */
  resource: LiveResource;
  /**
   * Refetch this surface's data through its normal authorized route.
   *
   * Called on subscribe, on tab refocus, and on a poll tick while
   * disconnected — every edge where a broadcast could have been missed.
   * Broadcast is at-most-once with no replay, so a subscriber that was offline
   * when an event fired never receives it; reconcile is what makes "no refresh
   * needed" true rather than "true unless you were away".
   *
   * Must be referentially stable or wrapped in useCallback.
   */
  onChange: () => void;
};

/**
 * Subscribe to a change signal and keep a surface current.
 *
 * Replaces the hand-rolled `.channel(...).on('postgres_changes', ...)` +
 * `setInterval` pairs: those poll blindly on a timer whether or not anything
 * changed, and silently deliver nothing when the subscriber's JWT lacks an RLS
 * grant on the table. This subscribes to a topic the server authorizes
 * explicitly, and reconciles on the edges instead of on a clock.
 *
 * Returns connection state for optional UI (a "reconnecting" hint); most
 * callers can ignore it.
 */
export function useLiveChannel({ topic, resource, onChange }: UseLiveChannelArgs): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  // The effect must not resubscribe when the callback identity or connection
  // status changes, so both are read through refs.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const connectedRef = useRef(false);

  useEffect(() => {
    if (!topic) return;

    let cancelled = false;
    connectedRef.current = false;
    setConnected(false);


    // Each refetch is a function invocation. A hidden tab defers until it is
    // seen again (the visibility handler reconciles anyway), and a burst of
    // events runs the first immediately and the rest as one trailing refetch.
    let lastRunAt = 0;
    let trailing: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      lastRunAt = Date.now();
      onChangeRef.current();
    };
    const request = () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      const wait = lastRunAt + COALESCE_MS - Date.now();
      if (wait <= 0) {
        run();
        return;
      }
      if (trailing) return;
      trailing = setTimeout(() => {
        trailing = null;
        if (!cancelled) run();
      }, wait);
    };

    // A unique channel instance per mount: two components subscribing to the
    // same topic must not share one channel, or the second .on() lands on an
    // already-subscribed channel and realtime-js throws.
    // Cast: the generated Database type narrows .on() to the 'system' overload
    // and does not admit 'broadcast'. Same reason the existing channels cast.
    // The client loads on demand so @supabase/ssr stays out of the shell
    // bundle; every page mounts this hook through the notification center.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let supabase: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channel: any = null;
    void getSupabaseBrowserClient().then((client) => {
      if (cancelled || !client) return;
      supabase = client;
      channel = client
      .channel(topic, { config: { private: true } })
      .on('broadcast', { event: LIVE_EVENT }, (message: { payload?: LiveChangePayload }) => {
        if (cancelled) return;
        if (message.payload?.resource !== resource) return;
        request();
      })
      .subscribe((status: string) => {
        if (cancelled) return;
        const isUp = status === 'SUBSCRIBED';
        connectedRef.current = isUp;
        setConnected(isUp);
        // Reconcile on every (re)connect: anything emitted while the socket was
        // down was not queued for us.
        if (isUp) request();
      });
    });

    // A backgrounded tab has its socket suspended by the browser and misses
    // events without ever reporting a disconnect.
    const onFocus = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      run();
    };
    document.addEventListener('visibilitychange', onFocus);

    const pollInterval = setInterval(() => {
      if (!cancelled && !connectedRef.current && document.visibilityState === 'visible') run();
    }, DISCONNECTED_POLL_MS);

    return () => {
      cancelled = true;
      if (trailing) clearTimeout(trailing);
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onFocus);
      if (supabase && channel) void supabase.removeChannel(channel);
    };
  }, [topic, resource]);

  return { connected };
}
